import type { TimeReportResultRow } from '@/lib/time-entries';

export interface ReportPerson {
  id: string;
  name: string;
}

/**
 * Who the Reports screen's Person dropdown offers: the people with hours in the
 * window and project on screen, read off a by-person summary of that window.
 *
 * Not the employee directory. The summary is scoped by the server the way the
 * report itself is, so it lists exactly the people this reader can see hours
 * for — a manager is not offered a colleague whose every answer would be zero,
 * and a zero reads as a fact. And it keeps somebody who has left: last month's
 * hours still belong to them, while an "active employees" list would already
 * have dropped them. [HW-955]
 *
 * The chosen person is always listed, even once the window holds none of their
 * hours: a Select whose value is missing from its items shows nothing, and the
 * report underneath would still be narrowed to them with nothing on screen
 * saying so. Their name then comes from `knownNames`, what earlier windows
 * called them.
 */
export function reportPeople(
  rows: readonly TimeReportResultRow[] | undefined,
  selectedId: string,
  knownNames: ReadonlyMap<string, string>
): ReportPerson[] {
  const people = new Map<string, string>();
  for (const row of rows ?? []) {
    const id = row.keys[0];
    // The residual bucket: hours with no person to name, not a person to pick.
    if (!id) continue;
    people.set(id, row.label[0] || knownNames.get(id) || 'Unnamed');
  }
  if (selectedId !== 'all' && !people.has(selectedId)) {
    people.set(selectedId, knownNames.get(selectedId) ?? 'Selected person');
  }
  return [...people]
    .map(([id, name]) => ({ id, name }))
    .sort((a, b) => a.name.localeCompare(b.name));
}
